// /**
//  * This module exports a function that filters an array of shipments according
//  * to a Map of filters.
//  */
//
import { getDateRangeById } from "../data/date-ranges";
import { getDateRangeById1 } from "../data/date-ranges-new";
import { getStartOfDay } from "../utilities/get-start-of-day";
import { searchBarFilter } from "../utilities/search-bar-filter";
import defaultIncomingShipmentsTableColumns from "../data/incoming-shipments-table-columns";
import { convertUnixDate } from "../utilities/convert-unix";

/**
 * Returns a function that checks if an object's value is in an array of options.
 * @example
 * const checkFoo = isInOptions("foo")
 * const object = { foo: "bar" }
 * checkFoo(object, ["bar", "baz"])  // returns true
 * checkFoo(object, ["baz"]) // returns false
 */
const isInOptions = (key) => (obj, options) => options.includes(obj[key]);

// An object of filter applications where each key points to a function
// that expects `shipment` and `value` to consider
const applyFilter = {
  loadType: isInOptions("loadType"),
  source: ({ source }, value) =>
    source.toLowerCase().includes(value.toLowerCase()),
  loadId: ({ loadId }, value) =>
    loadId.toLowerCase().includes(value.toLowerCase()),
  tempZone: isInOptions("tempZone"),
  status: isInOptions("status"),
  shipmentId: ({ shipmentId }, value) =>
    shipmentId.toLowerCase().includes(value.toLowerCase()),
  ocrVendor: isInOptions("ocrVendor"),
  scac: ({ scac }, value) => scac.toLowerCase().includes(value.toLowerCase()),
  dateRange: (shipment, value) => {
    for (const dateRange of value) {
      // If any date is in the range selected, return true
      const { max } = getDateRangeById(dateRange);
      for (const shipmentIndex in shipment.scheduledArrivals) {
        if (
          shipment.scheduledArrivals[shipmentIndex] >=
          getStartOfDay(shipment.timeZone, max - 1) &&
          shipment.scheduledArrivals[shipmentIndex] <
          getStartOfDay(shipment.timeZone, max)
        ) {
          return true;
        }
      }
    }
    return false;
  },
  dateRangeNew: (shipment, value) => {
    for (const dateRange of value) {
      // If any date is in the range selected, return true
      const { max } = getDateRangeById1(dateRange);

      // Check appointmentDate for SCIP data (format: "Sat May 31 2025")
      if (shipment.appointmentDate) {
        // Get today's date
        const today = new Date();
        // Add max days to today to get the target date
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + max);

        // Format target date to match the format in appointmentDate
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const formattedTargetDate = `${days[targetDate.getDay()]} ${months[targetDate.getMonth()]
          } ${targetDate.getDate()} ${targetDate.getFullYear()}`;

        // Check if the appointmentDate matches the target date
        if (shipment.appointmentDate === formattedTargetDate) {
          return true;
        }
      }

      // Check appointmentTime if it exists
      if (shipment.appointmentTime) {
        const startOfDay = convertUnixDate(
          getStartOfDay(shipment.time_zone, max),
          shipment.time_zone
        );
        const startOfNextDay = convertUnixDate(
          getStartOfDay(shipment.time_zone, max + 1),
          shipment.time_zone
        );

        if (
          shipment.appointmentTime >= startOfDay &&
          shipment.appointmentTime < startOfNextDay
        ) {
          return true;
        }
      }

      // Check scheduledArrivals
      for (const shipmentIndex in shipment.scheduledArrivals) {
        // For negative max values (like -7), check if the shipment date is between max days ago and today
        if (max < 0) {
          if (
            shipment.scheduledArrivals[shipmentIndex] >=
              getStartOfDay(shipment.timeZone, max) &&
            shipment.scheduledArrivals[shipmentIndex] <
              getStartOfDay(shipment.timeZone, 0)
          ) {
            return true;
          }
        } else {
          // For non-negative max values, use range-based check for full day
          if (
            shipment.scheduledArrivals[shipmentIndex] >=
              getStartOfDay(shipment.timeZone, max) &&
            shipment.scheduledArrivals[shipmentIndex] <
              getStartOfDay(shipment.timeZone, max + 1)
          ) {
            return true;
          }
        }
      }
    }
    return false;
  },
  asrsEligible: (item, value) => {
    if (value.length === 2) {
      return true;
    } else {
      if (value[0] === "Y" && item.asrsConfirmedUnits > 0) {
        return true;
      } else if (value[0] === "N" && item.asrsConfirmedUnits === 0) {
        return true;
      }
    }
    return false;
  },
  isvmi: (item, value) => {
    // If this is a VMI shipment (hasAsn = 'Y'), always show it regardless of isvmi filter
    if (item.hasAsn === 'Y') {
      return true;
    }

    // For non-VMI shipments, apply the existing isvmi filter logic
    if (value.length === 2) {
      return true;
    } else {
      // If Y is selected, show only VMI shipments (but ASN VMI shipments are handled above)
      if (value[0] === "Y" && item.isvmi === "Y") {
        return true;
      }
      // If N is selected, show only non-VMI shipments
      // Also include items where isVMI is null or undefined
      else if (
        value[0] === "N" &&
        (item.isvmi === "N" || item.isvmi === null || item.isvmi === undefined)
      ) {
        return true;
      }
    }
    return false;
  },
  allFields: (shipment, value) => {
    // If any field passes, return true
    const searchableFields = defaultIncomingShipmentsTableColumns.map(
      (column) => column.id
    );
    const dateKeys = [
      "scheduledArrivals",
      "expectedArrivalTime",
      "arrivalDatetime",
    ];
    return searchBarFilter(value, shipment, dateKeys, searchableFields);
  },
};

/**
 * This function filters an array of shipments according to a Map of filters.
 *
 * @param  incomingShipments          Array of incomingShipment objects (see `src/prop-types/incoming-shipment` to see shape)
 * @param  filters            Map of filter objects (see `src/hooks/use-shipments-filters`)
 * @param  data               String indicating the data source ("scip", "unscheduled", etc.)
 * @return filteredShipments  Array of filtered shipments
 */
const filterIncomingShipments = (
  filteredIncomingShipments,
  filters,
  data,
  region_id
) => {
  if (filters.size === 0) return filteredIncomingShipments;
  // Loop through and apply filter in the Map to the incomingShipments array
  if (data === "unscheduled") {
    const dateRangeFilter = filters.get("dateRangeNew");
    if (dateRangeFilter?.value) {
      // Filter unscheduled shipments
      filteredIncomingShipments = filteredIncomingShipments.filter(
        (incomingShipment) => {
          // Special case for Vendor shipments with handoff window dates
          if (
            incomingShipment.loadType === "Vendor" &&
            incomingShipment.handoffWindowStartDateUTC &&
            incomingShipment.handoffWindowEndDateUTC
          ) {
            for (const dateRange of dateRangeFilter.value) {
              const { max } = getDateRangeById1(dateRange);

              // Get the filter date (start of day for the selected date range)
              const filterDate =
                region_id && region_id === 1
                  ? getStartOfDay("America/Los_Angeles", max)
                  : getStartOfDay(incomingShipment.timeZone, max);


              if (dateRangeFilter.value[0] === "yesterday") {
                if (
                  getStartOfDay(incomingShipment.timeZone, -1) >=
                  incomingShipment.handoffWindowEndDateUTC &&
                  incomingShipment.handoffWindowStartDateUTC >= filterDate
                ) {
                  return true;
                }
              } else if (
                filterDate >= incomingShipment.handoffWindowStartDateUTC &&
                filterDate <= incomingShipment.handoffWindowEndDateUTC
              ) {
                return true;
              }

            }

            // If no date range matches, filter out this shipment
            return false;
          }

          // For non-Vendor shipments or those without handoff windows, use existing logic
          for (const dateRange of dateRangeFilter.value) {
            const { max } = getDateRangeById1(dateRange);

            // Get the filter date (start of day for the selected date range)
            const filterDate = getStartOfDay(incomingShipment.timeZone, max);

            // Check if filter date falls within handoff window range
            if (
              filterDate >= incomingShipment.departureTime &&
              filterDate <= incomingShipment.firstAppointmentTime
            ) {
              return true;
            }
          }

          // If no date range matches, filter out this shipment
          return false;
        }
      );
    }

    // Apply other filters
    for (const filter of filters) {
      const [key, { value }] = filter;
      if (key !== "dateRangeNew") {
        // Skip dateRangeNew as we've already handled it
        filteredIncomingShipments = filteredIncomingShipments.filter(
          (incomingShipment) => applyFilter[key](incomingShipment, value)
        );
      }
    }
  } else if (data === "scip") {
    // Apply dateRangeNew filter
    const dateRangeFilter = filters.get("dateRangeNew");
    if (dateRangeFilter?.value) {
      // Check if T-7 ("yesterday") is one of the selected filters
      if (dateRangeFilter.value.includes("yesterday")) {
        // If T-7 is selected, filter out any T-7 data completely
        // First, create a copy of the filter values without "yesterday"
        const nonT7Filters = dateRangeFilter.value.filter(
          (value) => value !== "yesterday"
        );

        if (nonT7Filters.length > 0) {
          // If there are other filters, only show data for those filters
          filteredIncomingShipments = filteredIncomingShipments.filter(
            (incomingShipment) =>
              applyFilter["dateRangeNew"](incomingShipment, nonT7Filters)
          );
        } else {
          // If T-7 was the only filter, return empty array
          return [];
        }
      } else {
        // Normal filtering if T-7 is not selected
        filteredIncomingShipments = filteredIncomingShipments.filter(
          (incomingShipment) =>
            applyFilter["dateRangeNew"](incomingShipment, dateRangeFilter.value)
        );
      }
    }

    // Apply loadType filter if it exists
    const loadTypeFilter = filters.get("loadType");
    if (loadTypeFilter?.value) {
      filteredIncomingShipments = filteredIncomingShipments.filter(
        (incomingShipment) => {
          // For SCIP data, sourceType is equivalent to loadType
          if (incomingShipment.sourceType) {
            return loadTypeFilter.value.includes(incomingShipment.sourceType);
          }
          return false;
        }
      );
    }

    // Apply tempZone filter if it exists
    const tempZoneFilter = filters.get("tempZone");
    if (tempZoneFilter?.value) {
      filteredIncomingShipments = filteredIncomingShipments.filter(
        (incomingShipment) => {
          // If tempZone doesn't exist, it's a total row, so we should exclude it when filtering by tempZone
          if (!incomingShipment.tempZone) {
            return true;
          }

          // For detailed SCIP data, check if the tempZone matches
          return tempZoneFilter.value.includes(incomingShipment.tempZone);
        }
      );
    }
  } else {
    for (const filter of filters) {
      const [key, { value }] = filter;
      filteredIncomingShipments = filteredIncomingShipments.filter(
        (incomingShipment) => applyFilter[key](incomingShipment, value)
      );
    }
  }
  return filteredIncomingShipments;
};

export default filterIncomingShipments;
